import { useSEO, generateWebPageSchema, generateBreadcrumbSchema, generateServiceSchema } from '@/utils/seo';
import ServicePageLayout, { ServicePageConfig } from '@/pages/service-common/ServicePageLayout';

const PLATFORMS = [
  { icon: 'ri-instagram-line', name: 'Instagram' },
  { icon: 'ri-twitter-x-line', name: 'X (Twitter)' },
  { icon: 'ri-youtube-fill', name: 'YouTube' },
  { icon: 'ri-tiktok-fill', name: 'TikTok' },
  { icon: 'ri-line-fill', name: 'LINE' },
  { icon: 'ri-linkedin-fill', name: 'LinkedIn' },
  { icon: 'ri-facebook-fill', name: 'Facebook' },
  { icon: 'ri-global-line', name: 'その他' },
];

const PlatformGrid = () => (
  <div className="mt-2">
    <div className="mb-6">
      <p className="text-xs tracking-widest uppercase font-medium mb-3" style={{ color: '#7C3AED' }}>Platforms</p>
      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">対応プラットフォーム</h2>
    </div>
    <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
      {PLATFORMS.map((p, i) => (
        <div
          key={i}
          className="flex flex-col items-center gap-2 p-4 rounded-xl border border-gray-100 transition-all duration-200 cursor-default"
          style={{ background: '#FAFAFA' }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = '#7C3AED'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = '#F3F4F6'; }}
        >
          <i className={`${p.icon} text-2xl`} style={{ color: '#7C3AED' }}></i>
          <span className="text-xs text-gray-500 text-center whitespace-nowrap">{p.name}</span>
        </div>
      ))}
    </div>
  </div>
);

/* ─── Hero Background ─── */
const HERO_IMG = 'https://readdy.ai/api/search-image?query=Elegant%20abstract%20golden%20social%20media%20icons%20and%20sharing%20network%20connections%20on%20deep%20navy%20blue%20background%20minimal%20sophisticated%20high-end%20corporate%20aesthetic%20soft%20glowing%20particles%20no%20text&width=1920&height=800&seq=snshero1&orientation=landscape';

const config: ServicePageConfig = {
  pageLabel: 'SNS・メディアマーケティング',
  heroImage: HERO_IMG,
  tag: 'SNS & Media Marketing',
  title: <>熱狂的なファンを<br />育てるSNS戦略</>,
  subtitle: '主要SNSプラットフォーム全てをカバー。コンテンツ戦略からコミュニティ運営まで、ブランドのオンライン存在感を最大化します。',
  icon: 'ri-share-circle-line',
  services: [
    { icon: 'ri-compass-3-line', title: 'SNS戦略設計', desc: 'ビジネス目標に連動した包括的なSNSマーケティング計画を策定します。' },
    { icon: 'ri-edit-2-line', title: 'コンテンツ制作', desc: '各プラットフォームの特性に最適化した、エンゲージメントの高いコンテンツを制作します。' },
    { icon: 'ri-team-line', title: 'コミュニティ管理', desc: 'ブランドファンのコミュニティを育成し、熱狂的な支持者を増やします。' },
    { icon: 'ri-user-star-line', title: 'インフルエンサーマーケティング', desc: '最適なインフルエンサーとのパートナーシップで、信頼性の高いリーチを実現します。' },
    { icon: 'ri-notification-3-line', title: 'ソーシャルリスニング', desc: 'ブランドへの言及やトレンドをリアルタイムで監視し、迅速な対応を支援します。' },
    { icon: 'ri-pie-chart-2-line', title: 'アナリティクス・改善', desc: 'SNSパフォーマンスを多角的に分析し、継続的な改善サイクルを構築します。' },
  ],
  benefits: [
    'フォロワーとの本物のつながりを構築し、ブランドへの信頼と愛着を醸成',
    '各プラットフォームのアルゴリズムを熟知したコンテンツ戦略で認知拡大',
    'SNSを強力な集客・転換チャネルとして機能させる仕組みを構築',
    'トレンドをいち早く察知し、最適なタイミングでの情報発信を実現',
    '全主要SNSプラットフォームを一元管理することでリソースを効率化',
    'インフルエンサーとの連携で、信頼性の高いバイラル効果を創出',
  ],
  extraSection: <PlatformGrid />,
};

export default function SNSMediaMarketingPage() {
  useSEO({
    title: '東京 SNS・メディアマーケティング TradFit株式会社 | ソーシャルメディア戦略',
    description: 'TradFit株式会社のSNS・メディアマーケティングサービス。主要SNSプラットフォーム全てをカバーし、ブランドのオンライン存在感を最大化します。',
    keywords: 'SNSマーケティング, ソーシャルメディア, インフルエンサーマーケティング, コンテンツ戦略, 東京',
    path: '/sns-media-marketing',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema('SNS・メディアマーケティング | TradFit株式会社', 'TradFit株式会社のSNS・メディアマーケティングサービス。', '/sns-media-marketing'),
        generateBreadcrumbSchema([{ name: 'ホーム', path: '/' }, { name: '事業内容', path: '/business' }, { name: 'SNS・メディアマーケティング', path: '/sns-media-marketing' }]),
        generateServiceSchema('SNS・メディアマーケティング', '主要SNSプラットフォーム全てをカバーしたSNS・メディアマーケティングサービス。'),
      ],
    },
  });
  return <ServicePageLayout config={config} />;
}
